import {
  BoxV2 as Box,
  ButtonV2 as Button,
  DropdownV3 as Dropdown,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useState } from 'react';
import { useForm } from 'react-hook-form';

import { FormLayout, FormSection, FormTitle } from './components';
import { useTrademarkVerificationControllers } from './hooks';
import { TrademarkVerificationFormValues } from './types';
import usePatentOfficeOptions from '../../hooks/usePatentOfficeOptions';
import { trademarkController } from '@/apis/core';

interface TrademarkVerificationFormProps {
  brandUid: string;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const TrademarkVerificationForm: FunctionComponent<
  TrademarkVerificationFormProps
> = ({ brandUid, onCancel, onSubmit }) => {
  const patentOfficeOptions = usePatentOfficeOptions();
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<TrademarkVerificationFormValues>({
    mode: 'onTouched',
    defaultValues: {
      registrationId: '',
      trademarkText: '',
      trademarkOwner: '',
      patentOffice: '',
    },
  });
  const {
    // Trademark Registration Number
    registrationId,
    setRegistrationId,
    validateRegistrationId,
    registrationIdError,
    // Trademark Wording
    trademarkText,
    setTrademarkText,
    validateTrademarkText,
    trademarkTextError,
    // Trademark Owner
    trademarkOwner,
    setTrademarkOwner,
    validateTrademarkOwner,
    trademarkOwnerError,
    // Trademark Office
    patentOffice,
    setPatentOffice,
    validatePatentOffice,
    patentOfficeError,
  } = useTrademarkVerificationControllers(control);
  const [proceeding, setProceeding] = useState(false);

  const handleValid = async (values: TrademarkVerificationFormValues) => {
    setProceeding(true);
    const response = await trademarkController.createTrademarks(
      brandUid,
      values
    );
    if (response.length > 0) {
      trademarkController.verifyTrademark(brandUid, response[0].uid);
      onSubmit?.();
    }
    setProceeding(false);
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <FormLayout>
        <FormTitle
          title="Apply for Trademark Verification"
          description="Enter the required information below to submit a trademark for verification."
        />
        <Box sx={{ color: 't.black90', fontSize: 'H100' }}>
          * Indicates Required Field
        </Box>
        <FormSection>
          <TextInput
            required
            error={!!registrationIdError?.message}
            label="Trademark Registration Number"
            note={registrationIdError?.message}
            value={registrationId}
            onChange={setRegistrationId}
            onBlur={validateRegistrationId}
            data-testid="registrationId"
          />
          <TextInput
            required
            error={!!trademarkTextError?.message}
            label="Trademark Wording"
            note={trademarkTextError?.message}
            value={trademarkText}
            onChange={setTrademarkText}
            onBlur={validateTrademarkText}
            data-testid="trademarkText"
          />
          <TextInput
            error={!!trademarkOwnerError?.message}
            label="Trademark Owner"
            note={
              trademarkOwnerError?.message ||
              "*This is required if the Trademark Owner is different than the brand's legal name."
            }
            value={trademarkOwner}
            onChange={setTrademarkOwner}
            onBlur={validateTrademarkOwner}
            data-testid="trademarkOwner"
          />
          <Dropdown
            required
            error={!!patentOfficeError?.message}
            label="Trademark Office"
            note={patentOfficeError?.message}
            options={patentOfficeOptions}
            value={patentOffice}
            onChange={setPatentOffice}
            data-testid="patentOffice"
          />
        </FormSection>
        <Flex sx={{ justifyContent: 'flex-end', gap: 'xl' }}>
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={proceeding}
            onClick={onCancel}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            shape="square"
            color="secondary"
            type="submit"
            disabled={!isValid || proceeding}
            data-testid="submit"
          >
            Submit
          </Button>
        </Flex>
      </FormLayout>
    </form>
  );
};

export default TrademarkVerificationForm;
